import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { fetchWeeklyComparison } from '../services/weeklyService';
import { useAuth } from '../context/useAuth'; // Import useAuth to access user context

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const WeeklyTrendChart = ({ stream }) => {
  const [chartData, setChartData] = useState({ datasets: [] });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth(); // Access user from context

  useEffect(() => {
    const loadData = async () => {
      try {
        
        const result = await fetchWeeklyComparison(stream);
        const { data, subcounties } = result;
        const allowedSubcounties = user?.sub_county_id 
    ? subcounties.filter(sc => sc.id === user.sub_county_id)
    : subcounties;
        // Sort data chronologically for the chart (ascending start_date)
        const sortedData = [...data].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

        // 1. Labels are the week start dates
        const labels = sortedData.map(week => 
          new Date(week.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        );

        // 2. Generate a unique color for each sub-county line
        const colors = [
          '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6366f1'
        ];

        // 3. Map each sub-county to a Chart.js dataset
        const datasets = allowedSubcounties.map((sc, index) => ({
          label: sc.name,
          data: sortedData.map(week => week[sc.name] || 0),
          borderColor: colors[index % colors.length],
          backgroundColor: colors[index % colors.length] + '40', // 25% opacity
          tension: 0.3,
          fill: false,
        }));

        setChartData({ labels, datasets });
      } catch (err) {
        console.error("Chart loading error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [stream, user.sub_county_id]);
/*
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: `5-Week Revenue Trend per Sub-County (${stream || 'All Streams'})`,
        font: { size: 16 }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Revenue (KES)' },
        ticks: {
          callback: (value) => value.toLocaleString()
        }
      }
    }
  };
*/
const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { 
      position: 'top',
      labels: {
        font: { size: 10 } // Reduced legend text size
      }
    },
    title: {
      display: true,
      text: `5-Week Revenue Trend per Sub-County (${stream || 'All Streams'})`,
      font: { size: 13, weight: 'bold' } // Reduced title size
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      bodyFont: { size: 10 }, // Reduced tooltip body text
      titleFont: { size: 11 } // Reduced tooltip title text
    },
  },
  scales: {
    x: {
      ticks: {
        font: { size: 9 } // Reduced X-axis (dates) text size
      }
    },
    y: {
      beginAtZero: true,
      title: { 
        display: true, 
        text: 'Revenue (KES)',
        font: { size: 10 } // Reduced Y-axis title size
      },
      ticks: {
        font: { size: 9 }, // Reduced Y-axis numbers text size
        callback: function(value) {
          // Abbreviation Logic: Billions, Millions, Thousands
          if (value >= 1e9) return (value / 1e9).toFixed(1) + 'B';
          if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
          if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
          return value;
        }
      }
    }
  }
};
  if (loading) return <div className="h-64 flex items-center justify-center">Loading Trends...</div>;

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
      <div className="h-[400px]">
        <Line options={options} data={chartData} />
      </div>
    </div>
  );
};

export default WeeklyTrendChart;